// Таблицы (4.9): регистрация команд, ленты, контекстного меню, диалогов, вставки из Excel/Word/CSV и живых правил.
import type { Ctx, RegistryApi, LintIssue } from '../../../core/registry';
import { focusInside } from '../../../core/editor/insert';
import { readTextFile } from '../util';
import {
  alignColumns, fillEmptyWithDash, hasNppColumn, removeNppColumn, setHeaderRows, setWidths, sortRows, tableCtx,
  tableCtxAt, toggleDash, cells, NUMERIC_RE, type Align,
} from './model';
import { hasTable, isTableOnlyHtml, looksLikeMarkdownTable, looksLikeTsv, parseHtmlTable, parseTextTable } from './parse';
import { insertTable, pasteIntoTable } from './insert';
import { InsertTableDialog, TableGrid, TablePropsDialog, TextToTableDialog } from './dialogs';
import { TfcTablesExtension } from './plugin';
import { TableToolbarExtension } from './toolbar';

const inTable = (c: Ctx) => !!c.editor && !!tableCtx(c.editor);

export function setupTables(r: RegistryApi) {
  // ---------------------------------------------------------------------------------------- команды
  r.commands.add({ id: 'insert.table', title: 'Таблица', icon: 'table', description: 'Таблица по СТО (4.9)', run: (c, arg) => {
    const [rr, cc] = (arg as [number, number]) || [3, 3];
    if (!c.editor) return;
    insertTable(c.editor, { rows: Array.from({ length: Math.max(2, rr) }, () => Array.from({ length: Math.max(1, cc) }, () => ({ content: [{ type: 'paragraph' }], colspan: 1, rowspan: 1 }))), headerRows: 1 });
  } });
  r.commands.add({ id: 'table.insertDialog', title: 'Вставить таблицу…', icon: 'table-props', shortcut: 'Mod-Alt-t',
    description: 'Число строк и граф, строк головки, название', run: (c) => c.openDialog('tfc.insertTable') });
  r.commands.add({ id: 'table.fromText', title: 'Таблица из текста, CSV или Excel…', icon: 'tfc-text-to-table',
    description: 'Выделенные абзацы (графы через табуляцию, «;», «,» или «|») или вставленные данные -> таблица', run: (c) => textToTable(c) });

  const t = (id: string, title: string, icon: string, fn: (c: Ctx, arg?: unknown) => unknown, extra: Record<string, unknown> = {}) =>
    r.commands.add({ id: 'table.' + id, title, icon, run: fn, isEnabled: inTable, ...extra });
  t('rowAbove', 'Строка выше', 'tfc-row-above', (c) => c.editor?.chain().focus().addRowBefore().run());
  t('rowBelow', 'Строка ниже', 'tfc-row-below', (c) => c.editor?.chain().focus().addRowAfter().run());
  t('colLeft', 'Графа слева', 'tfc-col-left', (c) => c.editor?.chain().focus().addColumnBefore().run());
  t('colRight', 'Графа справа', 'tfc-col-right', (c) => c.editor?.chain().focus().addColumnAfter().run());
  t('delRow', 'Удалить строку', 'minus', (c) => c.editor?.chain().focus().deleteRow().run());
  t('delCol', 'Удалить графу', 'minus', (c) => c.editor?.chain().focus().deleteColumn().run());
  t('merge', 'Объединить ячейки', 'tfc-merge', (c) => {
    if (!c.editor?.chain().focus().mergeCells().run()) c.toast('Выделите несколько ячеек мышью (или Shift+стрелки) и объедините', 'info');
  });
  t('split', 'Разделить ячейку', 'tfc-split', (c) => {
    if (!c.editor?.chain().focus().splitCell().run()) c.toast('Разделить можно только объединённую ячейку', 'info');
  });
  t('headerRows', 'Строк головки', 'tfc-header', (c, n) => c.editor && setHeaderRows(c.editor, Number(n) || 1));
  t('header', 'Строки головки…', 'tfc-header', (c) => c.openDialog('tfc.tableProps'));
  t('align', 'Выравнивание графы', 'align-center', (c, al) => {
    if (al === 'right') { c.toast('Значения граф выравнивают по центру (4.9.23) - вправо по СТО нельзя', 'info'); al = 'center'; }
    if (c.editor) alignColumns(c.editor, (al ?? null) as Align);
  });
  for (const [al, title] of [['left', 'влево'], ['center', 'по центру']] as const) {
    t('align-' + al, 'Выровнять графу ' + title, 'align-' + al, (c) => c.editor && alignColumns(c.editor, al));
  }
  t('align-auto', 'Выравнивание графы по СТО (авто)', 'tfc-magic', (c) => c.editor && alignColumns(c.editor, null));
  t('dash', 'Прочерк «–»', 'tfc-dash', (c) => {
    if (c.editor && !toggleDash(c.editor)) c.toast('Прочерк ставится в пустую ячейку (при отсутствии данных, 4.9.21)', 'info');
  });
  t('fillDash', 'Прочерк во все пустые ячейки', 'tfc-dash', (c) => {
    const n = c.editor ? fillEmptyWithDash(c.editor) : 0;
    c.toast(n ? `Прочерков поставлено: ${n}` : 'Пустых ячеек нет', n ? 'ok' : 'info', 2500);
  });
  t('note', 'Примечание к таблице', 'note', (c) => {
    const ed = c.editor!;
    const tc = tableCtx(ed);
    if (!tc) return;
    const n = tc.sto;
    const end = tc.stoPos + n.nodeSize - 1;
    if (n.lastChild?.type.name === 'tableNote') { focusInside(ed, end - n.lastChild.nodeSize); return; }
    ed.chain().insertContentAt(end, { type: 'tableNote', content: [{ type: 'paragraph' }] }).run();
    focusInside(ed, end);
  }, { description: 'Последней строкой таблицы: «Примечание – …» или «Примечания» с номерами (4.10.7)' });
  t('sortAsc', 'Сортировать по графе: А → Я, 0 → 9', 'tfc-sort-az', (c) => { const e = c.editor && sortRows(c.editor, 1); if (e) c.toast(e, 'warn'); });
  t('sortDesc', 'Сортировать по графе: Я → А, 9 → 0', 'tfc-sort-za', (c) => { const e = c.editor && sortRows(c.editor, -1); if (e) c.toast(e, 'warn'); });
  t('widthsEqual', 'Ширины граф: равные', 'tfc-cols-equal', (c) => c.editor && setWidths(c.editor, 'equal'));
  t('widthsAuto', 'Ширины граф: по содержимому', 'tfc-width', (c) => c.editor && setWidths(c.editor, 'auto'));
  t('props', 'Свойства таблицы…', 'table-props', (c) => c.openDialog('tfc.tableProps'), { description: 'Головка, ширины граф в сантиметрах, выравнивание граф' });
  t('pasteData', 'Вставить данные из Excel/CSV в таблицу…', 'tfc-csv', (c) => c.openDialog('tfc.textTable', { intoTable: true }));
  t('removeNpp', 'Убрать графу «№ п/п» (номера - в боковик)', 'tfc-magic', (c) => c.editor && removeNppColumn(c.editor));
  t('delete', 'Удалить таблицу', 'trash', (c) => {
    const tc = c.editor && tableCtx(c.editor);
    if (!tc) return;
    c.editor!.chain().focus().deleteRange({ from: tc.stoPos, to: tc.stoPos + tc.sto.nodeSize }).run();
  });

  // ---------------------------------------------------------------------------------------- лента
  r.ribbonGroups.add({ id: 'insert.objects', tab: 'insert', title: 'Иллюстрации и таблицы', order: 10 });
  r.ribbonItems.add({ id: 'table', group: 'insert.objects', type: 'custom', order: 20, render: (c) => <TableGrid ctx={c} /> });

  r.ribbonTabs.add({ id: 'table', title: 'Таблица', order: 55, contextual: inTable });
  r.ribbonGroups.add({ id: 'table.rows', tab: 'table', title: 'Строки и графы', order: 10 });
  r.ribbonGroups.add({ id: 'table.cells', tab: 'table', title: 'Ячейки', order: 20 });
  r.ribbonGroups.add({ id: 'table.align', tab: 'table', title: 'Выравнивание графы', order: 25 });
  r.ribbonGroups.add({ id: 'table.table', tab: 'table', title: 'Таблица', order: 30 });
  r.ribbonGroups.add({ id: 'table.data', tab: 'table', title: 'Данные', order: 40 });
  ['rowAbove', 'rowBelow', 'colLeft', 'colRight', 'delRow', 'delCol'].forEach((id, i) =>
    r.ribbonItems.add({ id: 't-' + id, group: 'table.rows', type: 'button', command: 'table.' + id, label: r.commands.get('table.' + id)!.title, order: i }));
  ['merge', 'split', 'dash'].forEach((id, i) =>
    r.ribbonItems.add({ id: 't-' + id, group: 'table.cells', type: 'button', command: 'table.' + id, label: r.commands.get('table.' + id)!.title, order: i }));
  ['align-left', 'align-center', 'align-auto'].forEach((id, i) =>
    r.ribbonItems.add({ id: 't-' + id, group: 'table.align', type: 'button', command: 'table.' + id, order: i }));
  r.ribbonItems.add({ id: 't-header', group: 'table.table', type: 'menu', icon: 'tfc-header', label: 'Головка', order: 0, hint: 'Строк в головке (4.9.8, повтор на каждой странице)',
    items: (c) => { const tc = c.editor && tableCtx(c.editor); return [1, 2, 3].map((n) => ({ label: `Строк головки: ${n}`, command: 'table.headerRows', arg: n, checked: tc?.headerRows === n })); } });
  ['note', 'props', 'delete'].forEach((id, i) =>
    r.ribbonItems.add({ id: 't-' + id, group: 'table.table', type: 'large', command: 'table.' + id, order: i + 1 }));
  r.ribbonItems.add({ id: 't-data', group: 'table.data', type: 'menu', icon: 'tfc-sort-az', label: 'Данные', order: 0,
    items: [{ command: 'table.sortAsc' }, { command: 'table.sortDesc' }, { separator: true }, { command: 'table.widthsEqual' }, { command: 'table.widthsAuto' },
      { command: 'table.fillDash' }, { separator: true }, { command: 'table.pasteData' }] });

  // ---------------------------------------------------------------------------------------- контекстное меню
  r.contextMenu.add({ id: 'tfc.table', order: 10, items: (_c, target) => {
    if (!target.inTable) return null;
    return [
      { label: 'Вставить', icon: 'plus', children: [{ command: 'table.rowAbove' }, { command: 'table.rowBelow' }, { command: 'table.colLeft' }, { command: 'table.colRight' }] },
      { label: 'Удалить', icon: 'minus', children: [{ command: 'table.delRow' }, { command: 'table.delCol' }, { separator: true }, { command: 'table.delete' }] },
      { separator: true },
      { command: 'table.merge' }, { command: 'table.split' }, { command: 'table.dash' },
      { label: 'Выравнивание графы', icon: 'align-center', children: [{ command: 'table.align-left' }, { command: 'table.align-center' }, { command: 'table.align-auto' }] },
      { label: 'Строк головки', icon: 'tfc-header', children: [1, 2, 3].map((n) => ({ label: String(n), command: 'table.headerRows', arg: n })) },
      { separator: true },
      { command: 'table.note' }, { command: 'table.sortAsc' }, { command: 'table.props' },
    ];
  } });

  r.dialogs.add({ id: 'tfc.insertTable', component: InsertTableDialog });
  r.dialogs.add({ id: 'tfc.textTable', component: TextToTableDialog });
  r.dialogs.add({ id: 'tfc.tableProps', component: TablePropsDialog });
  r.extensions.add({ id: 'tfc.tables', create: () => [TfcTablesExtension, TableToolbarExtension] });

  // ---------------------------------------------------------------------------------------- вставка
  // HTML-таблица (Excel, Google Таблицы, LibreOffice, таблица из Word/браузера целиком) - новая таблица СТО;
  // курсор в таблице - данные заполняют ячейки с текущей (как в Excel)
  r.paste.add({ id: 'tfc.tableHtml', priority: 85,
    match: (d, c) => d.kind === 'paste' && hasTable(d.html) && !/<img[^>]+src="?(file:|data:image)/i.test(d.html) && (isTableOnlyHtml(d.html) || inTable(c)),
    handle: (d, c) => {
      const m = parseHtmlTable(d.html, c.editor!.schema);
      if (!m || !m.rows.length) return false;
      // одна ячейка Excel вне таблицы - это просто текст
      if (m.rows.length === 1 && m.rows[0].length === 1 && !inTable(c)) {
        c.editor!.chain().focus().insertContent(m.rows[0][0].content[0]?.content || []).run();
        return true;
      }
      return placeMatrix(c, m, 'html');
    } });
  // текст: TSV (Excel без HTML), Markdown-таблица
  r.paste.add({ id: 'tfc.tableText', priority: 45,
    match: (d, c) => d.kind === 'paste' && !d.files.length && (!d.html || inTable(c)) && (looksLikeTsv(d.text) || looksLikeMarkdownTable(d.text)),
    handle: (d, c) => {
      const m = parseTextTable(d.text);
      if (!m) return false;
      if (!inTable(c) && m.rows.length < 2) return false;      // одна строка с табуляциями - обычный текст
      return placeMatrix(c, m, 'text');
    } });
  // файл .csv / .tsv (перетаскивание или вставка файла)
  r.paste.add({ id: 'tfc.csvFile', priority: 95,
    match: (d) => d.files.length > 0 && d.files.every((f) => /\.(csv|tsv)$/i.test(f.name) || /text\/(csv|tab-separated-values)/.test(f.type)),
    handle: async (d, c) => {
      for (const f of d.files) {
        const m = parseTextTable(await readTextFile(f));
        if (m) {
          if (d.pos !== null && c.editor) c.editor.commands.setTextSelection(d.pos);
          placeMatrix(c, m, 'csv');
        }
      }
    } });

  // ---------------------------------------------------------------------------------------- живые правила
  r.lint.add({ id: 'tfc.tables', title: 'Таблицы', run: (ix) => {
    const out: LintIssue[] = [];
    for (const e of ix.entries) {
      if (e.type !== 'stoTable') continue;
      const tc = tableCtxAt(ix.doc, e.pos);
      if (!tc) continue;
      const label = e.num?.label || 'Таблица';
      // выравнивание «вправо» (из старых проектов или «как в исходнике»): sto_check - E TBL-COL-ALIGN (4.9.23)
      if (cells(tc).some((x) => x.row >= tc.headerRows && x.node.attrs.align === 'right')) {
        out.push({ sev: 'E', code: 'TBL-COL-ALIGN', node: e.id, rule: '4.9.23', msg: `${label}: значения показателей выравнивают по центру графы, а не вправо`,
          fix: { label: 'По центру', run: (c) => { const at = findPos(c, e.id); if (c.editor && at !== undefined) alignRightToCenter(c.editor, at); } } });
      }
      if (hasNppColumn(tc.table)) {
        out.push({ sev: 'E', node: e.id, rule: '4.9.13', msg: `${label}: графа «№ п/п» не допускается - номера пишут в боковике перед наименованием`,
          fix: { label: 'Убрать графу, номера - в боковик', run: (c) => c.editor && removeNppColumn(c.editor, findPos(c, e.id)) } });
      }
      const all = cells(tc);
      const head = all.filter((x) => x.row < tc.headerRows).map((x) => x.node.textContent.trim());
      // точка сокращения («Ед. изм.», «Кол.», «т. п.») - не «точка в конце заголовка графы»
      if (head.some((h) => /[^.]\.$/.test(h) && !/(?:^|[^А-Яа-яЁёA-Za-z])(т\.\s?[дпе]|др|пр|см|ед|изм|шт|руб|кол|тыс|млн|млрд|макс|мин|ср|сут|знач|напр|обозн|наим|характ|техн|кВт|мм|см|км|кг|сек|мин|вт|ч|с|г|т)\.$/i.test(h)))
        out.push({ sev: 'W', node: e.id, rule: '4.9.8', msg: `${label}: в конце заголовков граф точку не ставят` });
      if (head.some((h) => /^примечани[ея]$/i.test(h)))
        out.push({ sev: 'W', node: e.id, rule: '4.9.26', msg: `${label}: графу «Примечание» включать не рекомендуется - примечание пишут под таблицей (Таблица → Примечание)` });
      if (tc.map.height - tc.headerRows < 1) out.push({ sev: 'W', node: e.id, rule: '4.9.8', msg: `${label}: нет строк с данными` });
      const data = all.filter((x) => x.row >= tc.headerRows);
      const empty = data.filter((x) => x.col > 0 && !x.node.textContent.trim() && !hasAtom(x.node));
      // таблица-заготовка (все ячейки данных пустые) экспортом прочерками не заполняется (nodes_objects.py - так же)
      if (empty.length && empty.length < data.filter((x) => x.col > 0).length) out.push({ sev: 'I', node: e.id, rule: '4.9.21',
        msg: `${label}: в пустых ячейках (${empty.length}) при экспорте будет прочерк «–»`,
        fix: { label: 'Поставить прочерки сейчас', run: (c) => c.editor && fillEmptyWithDash(c.editor, findPos(c, e.id)) } });
      if (data.some((x) => /^([»«"〃„]+|То\s+же.*)$/.test(x.node.textContent.trim())))
        out.push({ sev: 'E', node: e.id, rule: '4.9.20–4.9.21', msg: `${label}: в таблице с линиями повторяющийся текст пишут полностью (без кавычек и «То же»)` });
      // одинаковое число десятичных знаков в графе (4.9.24)
      for (let col = 0; col < tc.map.width; col++) {
        const decs = new Set(data.filter((x) => x.col === col).map((x) => x.node.textContent.trim()).filter((v) => /^[+–−-]?\d+(,\d+)?$/.test(v))
          .map((v) => (v.includes(',') ? v.split(',')[1].length : 0)));
        if (decs.size > 1) { out.push({ sev: 'W', node: e.id, rule: '4.9.24', msg: `${label}, графа ${col + 1}: разное число десятичных знаков` }); break; }
      }
      if (data.some((x) => isDotDecimal(x.node.textContent.trim())))
        out.push({ sev: 'W', node: e.id, rule: '4.9.24', msg: `${label}: десятичный разделитель - запятая («0,5», а не «0.5»)`,
          fix: { label: 'Заменить на запятую во всей таблице', run: (c) => c.editor && decimalCommas(c.editor, findPos(c, e.id)) } });
      if (tc.map.height <= 3 && tc.map.width <= 2 && tc.map.height * tc.map.width <= 6)
        out.push({ sev: 'I', node: e.id, rule: '4.9.28', msg: `${label}: небольшой цифровой материал лучше дать текстом, а не таблицей` });
      if (tc.map.height > 20) out.push({ sev: 'I', node: e.id, rule: '4.9.11',
        msg: `${label}: длинная - при экспорте Word перенесёт её с надписью «Продолжение таблицы ${e.num?.num || 'N'}» и повторит головку` });
    }
    return out;
  } });
}

/** Число с десятичной точкой: «0.5», «-12.25» (не дата 01.02.2024 и не версия 1.2.3). */
export const isDotDecimal = (v: string) => /^[+−–-]?\d+\.\d+(?:[  ]?%)?$/.test(v);

/** «0.5» -> «0,5» во всех ячейках данных таблицы (одна правка). */
function decimalCommas(ed: any, pos: number | undefined): number {
  if (pos === undefined) return 0;
  const tc = tableCtxAt(ed.state.doc, pos);
  if (!tc) return 0;
  const tr = ed.state.tr;
  let n = 0;
  for (const x of cells(tc)) {
    if (x.row < tc.headerRows) continue;
    x.node.descendants((d: any, p: number) => {
      if (!d.isText) return true;
      const t = d.text || '';
      if (!isDotDecimal(x.node.textContent.trim())) return false;
      const re = /(\d)\.(\d)/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(t))) {
        const at = tr.mapping.map(x.pos + 1 + p + m.index + 1);
        tr.insertText(',', at, at + 1);
        n++;
      }
      return false;
    });
  }
  if (n) ed.view.dispatch(tr);
  return n;
}

/** Вставка из Excel / CSV: десятичная точка в числовых ячейках -> запятая (4.6.2.11, примеры СТО). */
export function commaMatrix(m: { rows: any[][]; headerRows?: number }): number {
  let n = 0;
  const fix = (node: any) => {
    if (node.type === 'text' && typeof node.text === 'string' && isDotDecimal(node.text.trim())) {
      node.text = node.text.replace(/(\d)\.(\d)/, '$1,$2');
      n++;
    }
    (node.content || []).forEach(fix);
  };
  m.rows.forEach((r, i) => { if (i >= (m.headerRows ?? 1)) r.forEach((c: any) => (c.content || []).forEach(fix)); });
  return n;
}

/** Все ячейки данных с выравниванием «вправо» -> «по центру». */
function alignRightToCenter(ed: any, pos: number) {
  const tc = tableCtxAt(ed.state.doc, pos);
  if (!tc) return;
  const tr = ed.state.tr;
  for (const x of cells(tc)) if (x.node.attrs.align === 'right') tr.setNodeMarkup(x.pos, undefined, { ...x.node.attrs, align: 'center' });
  if (tr.docChanged) ed.view.dispatch(tr);
}

function hasAtom(n: any) {
  let a = false;
  n.descendants((d: any) => { if (d.isAtom && !d.isText) a = true; return !a; });
  return a;
}

function findPos(c: Ctx, id: string): number | undefined {
  let p: number | undefined;
  c.editor?.state.doc.descendants((n, pos) => { if (p !== undefined) return false; if (n.attrs?.id === id) { p = pos; return false; } return true; });
  return p;
}

/** Матрица -> в текущую таблицу (с ячейки курсора) или новой таблицей. */
function placeMatrix(c: Ctx, m: { rows: any[][]; headerRows: number }, src: 'html' | 'text' | 'csv'): boolean {
  const ed = c.editor;
  if (!ed) return false;
  const commas = commaMatrix(m);
  if (commas) setTimeout(() => c.toast(`Десятичные точки заменены запятыми: ${commas} (как в примерах СТО); Ctrl+Z - отменить вставку`, 'info', 5000), 50);
  if (tableCtx(ed)) {
    // курсор в названии/примечании таблицы - вставляем в первую ячейку данных
    pasteIntoTable(ed, m);
    c.toast(`Вставлено в таблицу: ${m.rows.length} × ${m.rows[0]?.length || 0}`, 'ok', 2500);
    return true;
  }
  insertTable(ed, m);
  const npp = (() => { const t = tableCtx(ed); return t ? hasNppColumn(t.table) : false; })();
  c.toast(`Таблица ${m.rows.length} × ${m.rows[0]?.length || 0} вставлена${src === 'csv' ? ' из файла' : ''} - введите название` +
    (npp ? '. Графа «№ п/п» по СТО не допускается - см. «Проверка»' : ''), npp ? 'warn' : 'ok', npp ? 6000 : 3000);
  return true;
}

/** «Таблица из текста»: выделенные абзацы -> диалог с предпросмотром; без выделения - пустой диалог. */
function textToTable(c: Ctx) {
  const ed = c.editor;
  if (!ed) return;
  const { from, to, empty } = ed.state.selection;
  if (!empty) {
    const $a = ed.state.doc.resolve(from); const $b = ed.state.doc.resolve(to);
    const start = $a.before(Math.min($a.depth, 1)); const end = $b.after(Math.min($b.depth, 1));
    const lines: string[] = [];
    ed.state.doc.nodesBetween(start, end, (n) => { if (n.type.name === 'paragraph') { lines.push(textWithTabs(n)); return false; } return true; });
    if (lines.length) return c.openDialog('tfc.textTable', { text: lines.join('\n'), replace: { from: start, to: end } });
  }
  return c.openDialog('tfc.textTable', {});
}

function textWithTabs(n: any): string {
  let s = '';
  n.forEach((ch: any) => { s += ch.isText ? ch.text : ch.type.name === 'hardBreak' ? '\t' : ch.textContent || ''; });
  return s;
}
